import type { AuthProvider } from '@refinedev/core'
import { AUTH_API_URL, SHARE_API_BASE_URL } from '@publisher/modules/api'
import { ROLES } from '@modules/types'

const isSharePage = () => window.location.pathname.startsWith('/share')

let preventRedirectAfterFailedLogin = false

export const authProvider: AuthProvider = {
  register: async ({ password, token }) => {
    try {
      const response = await fetch(`${AUTH_API_URL}/auth/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        return {
          success: false,
          error: {
            name: 'RegisterError',
            message: data.message || `Registration failed with status ${response.status}`,
          },
        }
      }

      if (data.error || data.success === false) {
        return {
          success: false,
          error: {
            name: 'RegisterError',
            message: data.message || data.error || 'Registration failed',
          },
        }
      }

      return {
        success: true,
        redirectTo: '/login',
        successNotification: {
          message: 'Registration Successful',
          description: 'You can login with your new password',
        },
      }
    } catch {
      return {
        success: false,
        error: {
          name: 'RegisterError',
          message: 'Network error. Please try again.',
        },
      }
    }
  },
  login: async ({ username, email, password, redirectTo, isShare }) => {
    try {
      if (!username && !email) {
        return {
          success: false,
          error: {
            name: 'ValidationError',
            message: 'Please enter your username or email',
          },
        }
      }

      if (!password) {
        return {
          success: false,
          error: {
            name: 'ValidationError',
            message: 'Please enter your password',
          },
        }
      }

      const authUrl = isShare || isSharePage() ? SHARE_API_BASE_URL : AUTH_API_URL
      const response = await fetch(`${authUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: username || email,
          password,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        let errorMessage = 'Invalid email or password'

        if (data.errors && typeof data.errors === 'object') {
          if (data.errors.password && Array.isArray(data.errors.password)) {
            const [firstError] = data.errors.password
            errorMessage = firstError
          } else if (data.errors.username && Array.isArray(data.errors.username)) {
            const [firstError] = data.errors.username
            errorMessage = firstError
          } else if (data.errors.general && Array.isArray(data.errors.general)) {
            const [firstError] = data.errors.general
            errorMessage = firstError
          } else if (data.message) {
            errorMessage = data.message
          }
        } else if (data.message) {
          errorMessage = data.message
        }

        preventRedirectAfterFailedLogin = true

        return {
          success: false,
          error: {
            name: 'LoginError',
            message: errorMessage,
          },
        }
      }

      let finalRedirectTo = '/request'

      if (data.roles?.includes(ROLES.ADMIN_PANEL_SHARED_REQUEST_USER)) {
        finalRedirectTo = redirectTo || '/share'
      } else if (redirectTo) {
        finalRedirectTo = redirectTo
      }

      return {
        success: true,
        redirectTo: finalRedirectTo,
      }
    } catch {
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: 'Network error. Please try again.',
        },
      }
    }
  },
  forgotPassword: async ({ email }) => {
    try {
      if (!email || !email.includes('@')) {
        return {
          success: false,
          error: {
            name: 'ValidationError',
            message: 'Please enter a valid email address',
          },
        }
      }

      const response = await fetch(`${AUTH_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        let errorMessage = 'Failed to send reset email'

        if (data.errors && typeof data.errors === 'object') {
          if (data.errors.password && Array.isArray(data.errors.password)) {
            const [firstError] = data.errors.password
            errorMessage = firstError
          } else if (data.errors.username && Array.isArray(data.errors.username)) {
            const [firstError] = data.errors.username
            errorMessage = firstError
          } else if (data.errors.general && Array.isArray(data.errors.general)) {
            const [firstError] = data.errors.general
            errorMessage = firstError
          } else if (data.message) {
            errorMessage = data.message
          }
        } else if (data.message) {
          errorMessage = data.message
        }

        return {
          success: false,
          error: {
            name: 'ForgotPasswordError',
            message: errorMessage,
          },
        }
      }

      return {
        success: true,
        successNotification: {
          message: 'Reset email sent',
          description: 'Please check your email for password reset instructions',
        },
      }
    } catch {
      return {
        success: false,
        error: {
          name: 'ForgotPasswordError',
          message: 'Network error. Please try again.',
        },
      }
    }
  },
  updatePassword: async params => {
    try {
      const { password } = params
      const { token } = params

      if (!token) {
        return {
          success: false,
          error: {
            name: 'ValidationError',
            message: 'Reset token is required',
          },
        }
      }

      const response = await fetch(`${AUTH_API_URL}/auth/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          password,
          token,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        let errorMessage = 'Failed to reset password'

        if (data.errors && typeof data.errors === 'object') {
          if (data.errors.password && Array.isArray(data.errors.password)) {
            const [firstError] = data.errors.password
            errorMessage = firstError
          } else if (data.errors.token && Array.isArray(data.errors.token)) {
            const [firstError] = data.errors.token
            errorMessage = firstError
          } else if (data.errors.general && Array.isArray(data.errors.general)) {
            const [firstError] = data.errors.general
            errorMessage = firstError
          } else if (data.message) {
            errorMessage = data.message
          }
        } else if (data.message) {
          errorMessage = data.message
        }

        return {
          success: false,
          error: {
            name: 'ResetPasswordError',
            message: errorMessage,
          },
        }
      }

      return {
        success: true,
        redirectTo: '/login',
        successNotification: {
          message: 'Password reset successful',
          description: 'You can now login with your new password',
        },
      }
    } catch {
      return {
        success: false,
        error: {
          name: 'ResetPasswordError',
          message: 'Network error. Please try again.',
        },
      }
    }
  },
  logout: async () => {
    try {
      const authUrl = isSharePage() ? SHARE_API_BASE_URL : AUTH_API_URL
      await fetch(`${authUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (error) {
      // Logout request failed
    }

    preventRedirectAfterFailedLogin = false

    return {
      success: true,
      redirectTo: '/login',
    }
  },
  check: async () => {
    try {
      if (preventRedirectAfterFailedLogin) {
        preventRedirectAfterFailedLogin = false
        return {
          authenticated: false,
        }
      }

      const authUrl = isSharePage() ? SHARE_API_BASE_URL : AUTH_API_URL
      const response = await fetch(`${authUrl}/auth/check`, {
        method: 'GET',
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()

        if (data.success && data.status) {
          preventRedirectAfterFailedLogin = false

          return {
            authenticated: true,
            user: {
              id: data.user_id,
              type: data.user_type,
            },
            roles: data.roles || [],
          }
        }
        return {
          authenticated: false,
        }
      }
      return {
        authenticated: false,
      }
    } catch {
      return {
        authenticated: false,
      }
    }
  },
  onError: async error => {
    return { error }
  },
}
